import noop from 'lodash/noop';

/**
 * Tracks user activity so layout-moving updates can run when interaction stops.
 * Also exposes the shared activity timestamp used by idle logout.
 */
let isDragging = false;
let lastActivityTime = Date.now();
let lastStoredActivityTime: number | null = null;
let LOGOUT_THRESHOLD: number | null = null;
let logout = noop;
const queuedHandlers: Array<() => void> = [];
const IDLE_THRESHOLD = 1000;
const SHARED_ACTIVITY_THROTTLE_MS = 30 * 1000;
const SHARED_ACTIVITY_STORAGE_KEY = 'mimir-last-activity';
// 'mousemove' rather than 'pointermove': synthetic MouseEvents, as dispatched by
// tests and by the host-panel postMessage bridge, do not produce pointer events.
const ACTIVITY_EVENTS = new Set(['mousemove', 'pointerdown', 'keydown', 'scroll']);

/**
 * Returns true if user is currently dragging or has been active recently.
 * WebSocket updates should be queued when this returns true.
 */
export function isUserActive() {
  return hasUserBeenActive(IDLE_THRESHOLD);
}

function hasUserBeenActive(threshold: number): boolean {
  const stored = localStorage.getItem(SHARED_ACTIVITY_STORAGE_KEY);
  if (stored !== null) {
    const parsed = Number(stored);
    if (Number.isFinite(parsed) && parsed > lastActivityTime) {
      lastActivityTime = parsed;
    }
  }

  const timeSinceActivity = Date.now() - lastActivityTime;
  return isDragging || timeSinceActivity < threshold;
}

export function setDragging(dragging: boolean) {
  // Native drag operations can remain stationary longer than IDLE_THRESHOLD
  // without producing mouse events, so activity events alone are insufficient.
  isDragging = dragging;
  if (!dragging) {
    processQueuedHandlers();
  }
}

/**
 * Websocket updates must be applied in the order they arrived. Deferring only on activity
 * would let a message that arrives during an idle window overtake messages already queued,
 * so a `folder_deleted` could be applied before the `folder_created` it supersedes and the
 * replayed create would resurrect the folder.
 */
export function shouldDeferWsUpdate() {
  return isUserActive() || queuedHandlers.length > 0;
}

export function runWhenInactive(handler: () => void) {
  queuedHandlers.push(handler);
}

function processQueuedHandlers() {
  const handlers = [...queuedHandlers];
  queuedHandlers.length = 0;

  handlers.forEach((handler) => {
    try {
      handler();
    } catch (error) {
      console.error('Error processing queued WebSocket message:', error);
    }
  });

  if (handlers.length > 0) {
    console.log(`Processed ${handlers.length} queued WebSocket messages after idle period`);
  }
}

export function markActivity() {
  const now = Date.now();
  lastActivityTime = now;
  if (lastStoredActivityTime === null || now - lastStoredActivityTime >= SHARED_ACTIVITY_THROTTLE_MS) {
    lastStoredActivityTime = lastActivityTime;
    localStorage.setItem(SHARED_ACTIVITY_STORAGE_KEY, String(now));
  }
}

function checkIdleAndProcess() {
  if (!isUserActive() && queuedHandlers.length > 0) {
    processQueuedHandlers();
  }
  if (LOGOUT_THRESHOLD !== null && !hasUserBeenActive(LOGOUT_THRESHOLD)) {
    logout();
  }
}

export function startActivityTrackingWithAutoLogout(logoutThreshold: number, logoutHandler: () => void) {
  LOGOUT_THRESHOLD = logoutThreshold;
  logout = logoutHandler;

  startActivityTracking();
}

export function startActivityTracking() {
  if (typeof window === 'undefined') {
    return;
  }

  for (const event of ACTIVITY_EVENTS) {
    window.addEventListener(event, markActivity, { passive: true, capture: true });
  }
  setInterval(checkIdleAndProcess, 1000);
}
